<script>
import Layout from "../../layouts/main";
import draggable from "vuedraggable";
import appConfig from "@/app.config";
import BannerService from '@/services/banner';
import ProductService from '@/services/product';
import { productDescription } from '@/utils/product';
import Multiselect from "vue-multiselect";

// Posições dos slots no site + medidas recomendadas (2x/retina).
// Referência: especificação de CMS do layout V3.
const POSITIONS = [
  { value: 'B1',  label: 'B1 · Carrossel da home',   desk: '1760 × 640 px',  mob: '686 × 344 px', note: 'Até 5 slides, autoplay' },
  { value: 'B2a', label: 'B2a · Entre seções (1º)',  desk: '2640 × 400 px',  mob: '686 × 240 px', note: '1 banner por slot' },
  { value: 'B2b', label: 'B2b · Entre seções (2º)',  desk: '2640 × 400 px',  mob: '686 × 240 px', note: '1 banner por slot' },
  { value: 'B3',  label: 'B3 · Hero editorial',      desk: '2640 × 840 px',  mob: '686 × 440 px', note: 'Home editorial' },
];

export default {
  page: {
    title: "Banners",
    meta: [{ name: "description", content: appConfig.description }]
  },
  components: { Layout, Multiselect, draggable },
  data() {
    return {
      title: "Banners",
      banners: [],
      bannerToRemove: '',
      bannerSelected: {},
      products: [],
      isLoadingProducts: false,
      seeWebImage: false,
      positions: POSITIONS,
      // Formulário do modal "Novo banner"
      newBanner: this.emptyBanner(),
      newBannerSaving: false,
    };
  },
  methods: {
    emptyBanner() {
      return {
        position: 'B1',
        title: '',
        alt: '',
        link: '',
        start_date: '',
        end_date: '',
        show_overlay: false,
        eyebrow: '',
        subtitle: '',
        cta_label: '',
        fileDesktop: null,
        fileMobile: null,
        previewDesktop: '',
        previewMobile: '',
      };
    },
    positionInfo(value) {
      return this.positions.find(p => p.value === value) || this.positions[0];
    },
    positionLabel(banner) {
      return (banner && banner.position) || 'B1';
    },
    periodLabel(banner) {
      if (!banner.start_date && !banner.end_date) return '';
      const fmt = (d) => new Date(d).toLocaleDateString('pt-BR');
      const now = new Date();
      if (banner.start_date && new Date(banner.start_date) > now) return `Agendado · a partir de ${fmt(banner.start_date)}`;
      if (banner.end_date && new Date(banner.end_date) < now) return `Expirado em ${fmt(banner.end_date)}`;
      if (banner.end_date) return `Até ${fmt(banner.end_date)}`;
      return `Desde ${fmt(banner.start_date)}`;
    },
    load(hideLoading = false) {
      let loader;
      if (!hideLoading) {
        loader = this.$loading.show();
      }

      BannerService.getAll().then(res => {
        this.banners = res.data;

        if (!hideLoading) {
          loader.hide();
        }
      }).catch(err => {
        this.$toast.error(err);
        if (!hideLoading) {
          loader.hide();
        }
      })
    },
    removeBanner() {
      this.$bvModal.hide('modal-remove');

      BannerService.remove(this.bannerToRemove._id).then(() => {
        this.load(true);
        this.$toast.open('Banner removido');
      }).catch(() => {
        this.$toast.error('Ocorreu um erro ao excluir o banner');
      })
    },

    /* ── Novo banner (modal único com os dois crops + campos do CMS) ── */
    openNewBanner() {
      this.newBanner = this.emptyBanner();
      this.$bvModal.show('modal-new');
    },
    pickDesktop() { this.$refs.fileDesktop.click(); },
    pickMobile()  { this.$refs.fileMobile.click(); },
    onDesktopChosen(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.newBanner.fileDesktop = file;
      this.newBanner.previewDesktop = URL.createObjectURL(file);
      event.target.value = '';
    },
    onMobileChosen(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.newBanner.fileMobile = file;
      this.newBanner.previewMobile = URL.createObjectURL(file);
      event.target.value = '';
    },
    async createBanner() {
      if (!this.newBanner.fileDesktop && !this.newBanner.fileMobile) {
        this.$toast.error('Envie ao menos a imagem desktop');
        return;
      }

      this.newBannerSaving = true;
      const loader = this.$loading.show();

      try {
        // 1. Cria o banner com a imagem mobile (campo image). Sem mobile,
        //    usa a desktop também como mobile (fallback melhor que vazio).
        const mobileFile = this.newBanner.fileMobile || this.newBanner.fileDesktop;
        const formData = new FormData();
        formData.append('file', mobileFile);
        const created = await BannerService.uploadBanner(formData);
        const bannerId = created.data._id;

        // 2. Imagem desktop (image_web)
        if (this.newBanner.fileDesktop) {
          const webData = new FormData();
          webData.append('file', this.newBanner.fileDesktop);
          await BannerService.uploadWebBanner(bannerId, webData);
        }

        // 3. Campos do CMS
        await BannerService.update({
          _id: bannerId,
          title: this.newBanner.title,
          alt: this.newBanner.alt,
          link: this.newBanner.link,
          position: this.newBanner.position,
          start_date: this.newBanner.start_date || null,
          end_date: this.newBanner.end_date || null,
          show_overlay: this.newBanner.show_overlay,
          eyebrow: this.newBanner.eyebrow,
          subtitle: this.newBanner.subtitle,
          cta_label: this.newBanner.cta_label,
        });

        this.$bvModal.hide('modal-new');
        this.load(true);
        this.$toast.open('Banner adicionado');
      } catch (err) {
        this.$toast.error('Ocorreu um erro ao criar o banner');
      } finally {
        this.newBannerSaving = false;
        loader.hide();
      }
    },

    /* ── Configurar banner existente ── */
    loadBannerConfig(banner) {
      this.bannerSelected = JSON.parse(JSON.stringify(banner));
      if (!this.bannerSelected.position) this.bannerSelected.position = 'B1';
      // datas em formato YYYY-MM-DD para o input type=date
      ['start_date', 'end_date'].forEach(field => {
        if (this.bannerSelected[field]) {
          this.bannerSelected[field] = String(this.bannerSelected[field]).slice(0, 10);
        }
      });
      this.bannerSelected.products = [];
      this.$bvModal.show('modal-config');

      if (banner.products && banner.products.length > 0) {
        ProductService.getProductsByBanner(banner._id).then(res => {
          this.products = (res.data.data || []).map(p => this.formatProduct(p));
          this.bannerSelected.products = this.products;
        })
      }
    },
    configBanner() {
      this.$bvModal.hide('modal-config');

      let loader = this.$loading.show();

      const data = {
        _id: this.bannerSelected._id,
        title: this.bannerSelected.title,
        alt: this.bannerSelected.alt,
        link: this.bannerSelected.link,
        position: this.bannerSelected.position,
        start_date: this.bannerSelected.start_date || null,
        end_date: this.bannerSelected.end_date || null,
        show_overlay: !!this.bannerSelected.show_overlay,
        eyebrow: this.bannerSelected.eyebrow,
        subtitle: this.bannerSelected.subtitle,
        cta_label: this.bannerSelected.cta_label,
        products: [],
      }

      this.bannerSelected.products.forEach(product => {
        data.products.push(product._id);
      });

      BannerService.update(data).then(() => {
        this.load(true);

        this.$toast.open('Banner atualizado');
        loader.hide();
      }).catch(() => {
        this.$toast.error('Ocorreu um erro ao atualizar o banner');
        loader.hide();
      })
    },
    formatProduct(product) {
      // O rótulo do multiselect usa fullDescription; acrescentamos a cor para
      // diferenciar produtos que só mudam de cor.
      return {
        ...product,
        fullDescription: productDescription(product),
      };
    },
    findProducts(term) {
      this.isLoadingProducts = true;

      ProductService.getAllProducts(1, term).then(res => {
        this.isLoadingProducts = false;

        this.products = (res.data.data || []).map(p => this.formatProduct(p));
      }).catch(() => {
        this.isLoadingProducts = false;
      })
    },
    inactivate(banner) {
      let loader = this.$loading.show();

      BannerService.update({
        _id: banner._id,
        active: false,
      }).then(() => {
        this.load(true);

        this.$toast.open('Banner inativado');
        loader.hide();
      }).catch(() => {
        this.$toast.error('Ocorreu um erro ao inativar o banner');
        loader.hide();
      })
    },
    activate(banner) {
      let loader = this.$loading.show();

      BannerService.update({
        _id: banner._id,
        active: true,
      }).then(() => {
        this.load(true);

        this.$toast.open('Banner ativado');
        loader.hide();
      }).catch(() => {
        this.$toast.error('Ocorreu um erro ao ativar o banner');
        loader.hide();
      })
    },
    onDragImage() {
      const data = this.banners.map(item => item._id);
      BannerService.sort(data).then(() => {
        this.load(true);
      }).catch(() => {
      })
    },
    // Atualiza só a imagem web de um banner existente (ação do menu do card)
    handleFilesUpload(){
      const files = this.$refs.file.files;

      for( var i = 0; i < files.length; i++ ) {
        let file = files[i];

        let formData = new FormData();
        formData.append('file', file);

        let loader = this.$loading.show();

        if (this.bannerSelected && this.bannerSelected.isWebImage) {
          BannerService.uploadWebBanner(this.bannerSelected._id, formData).then(() => {
            this.load(true);
            loader.hide();

            this.$toast.open('Imagem web atualizada');
          }).catch(() => {
            this.$toast.error('Ocorreu um erro ao enviar a imagem');
            loader.hide();
          })
        }
      }
    },
    addImageWeb(banner) {
      this.bannerSelected = {
        ...banner,
        isWebImage: true,
      };

      this.$refs.file.click();
    }
  },
  mounted() {
    this.load();
  }
};
</script>

<template>
  <Layout>
    <div class="row">
      <div class="col-8">
        <div class="page-title-box ">
          <h4 class="mb-0 font-size-18">{{title}}</h4>
          <p class="card-title-desc mb-0">Banners exibidos no site e no aplicativo, por posição (slot). Envie os dois crops: desktop e mobile.</p>

          <b-form-checkbox v-model="seeWebImage" switch class="mt-2">
            <label>Visualizar versão web</label>
          </b-form-checkbox>
        </div>
      </div>

      <div class="col-4 align-items-center justify-content-end d-flex">
        <div class="text-right">
          <button type="button" class="btn btn-primary btn-rounded mb-2 mr-2" @click="openNewBanner">
            <i class="mdi mdi-plus mr-1"></i> Novo Banner
          </button>

          <input type="file" accept="image/*" name="file" ref="file" v-on:change="handleFilesUpload" />
        </div>
      </div>
    </div>

    <div class="row">
      <draggable :list="banners" class="banners-drag" @end="onDragImage">
        <div v-for="banner in banners" :key="banner._id" class="col-xl-4 col-sm-6">
          <div class="card">
            <div class="card-title">
              <p class="text-truncate ml-4 mb-0">
                <span class="badge badge-soft-primary mr-2">{{ positionLabel(banner) }}</span>
                <a href="javascript: void(0);" class="text-dark">{{banner.title}}</a>
              </p>

              <b-dropdown
                class="m-0"
                toggle-class="btn btn-link text-muted"
                right
                variant="white"
              >
                <template #button-content>
                  <i class="mdi mdi-dots-vertical font-size-20"></i>
                </template>
                <b-dropdown-item @click="loadBannerConfig(banner)">
                  <i class="fas fa-cog text-primary mr-2 mt-2"></i> Configurar
                </b-dropdown-item>
                <b-dropdown-item href="#" v-if="banner.active" @click="inactivate(banner)">
                  <i class="fas fa-pause-circle text-danger mr-2 mt-2"></i> Inativar
                </b-dropdown-item>
                <b-dropdown-item href="#" v-if="!banner.active"  @click="activate(banner)">
                  <i class="fas fa-play-circle text-success mr-2 mt-2"></i> Ativar
                </b-dropdown-item>
                <b-dropdown-item v-b-modal.modal-remove @click="bannerToRemove = banner">
                  <i class="fas fa-trash-alt text-danger mr-2 mt-2"></i> Excluir
                </b-dropdown-item>
                <b-dropdown-item @click="addImageWeb(banner)">
                  <i class="fas fa-image text-success mr-2 mt-2"></i> Atualizar imagem web
                </b-dropdown-item>
              </b-dropdown>
            </div>
            <div class="card-body card-banner">
              <div class="product-img position-relative">
                <img v-if="!seeWebImage" :src="`${banner.image}`" alt class="img-fluid mx-auto d-block" :class="{ 'greyscale': !banner.active }" />
                <img v-if="seeWebImage" :src="`${banner.image_web || banner.image}`" alt class="img-fluid mx-auto d-block" :class="{ 'greyscale': !banner.active, 'missing-web': !banner.image_web }" />
                <p v-if="seeWebImage && !banner.image_web" class="text-danger small mb-0 mt-1">Sem imagem web — o site exibirá o crop mobile.</p>
              </div>
              <p v-if="periodLabel(banner)" class="text-muted small mb-0 mt-2">
                <i class="mdi mdi-calendar-clock mr-1"></i>{{ periodLabel(banner) }}
              </p>
            </div>
          </div>
        </div>
      </draggable>
    </div>

    <b-modal
      id="modal-remove"
      title="Excluir Banner?"
      title-class="font-18"
      centered
    >
      <p>Confirma a exclusão do banner?</p>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-remove')" class="mr-3">Cancelar</b-btn>
        <b-btn variant="primary" @click="removeBanner">Excluir</b-btn>
      </div>
    </b-modal>

    <!-- ═══ Novo banner: os dois crops + campos do CMS num fluxo único ═══ -->
    <b-modal id="modal-new" title="Novo Banner" title-class="font-18" size="lg" centered>
      <div class="row">
        <div class="col-sm-6">
          <div class="form-group">
            <label>Posição no site</label>
            <select class="form-control" v-model="newBanner.position">
              <option v-for="p in positions" :key="p.value" :value="p.value">{{ p.label }}</option>
            </select>
            <small class="text-muted">
              Desktop {{ positionInfo(newBanner.position).desk }} ·
              Mobile {{ positionInfo(newBanner.position).mob }} ·
              {{ positionInfo(newBanner.position).note }}
            </small>
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label>Título</label>
            <input type="text" class="form-control" v-model="newBanner.title" placeholder="Ex.: Coleção Mediterrâneo" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <label>Imagem desktop</label>
          <div class="banner-drop" @click="pickDesktop">
            <img v-if="newBanner.previewDesktop" :src="newBanner.previewDesktop" class="img-fluid" />
            <div v-else class="banner-drop-hint">
              <i class="mdi mdi-monitor font-size-24"></i>
              <span>Selecionar imagem<br/><small>{{ positionInfo(newBanner.position).desk }}</small></span>
            </div>
          </div>
          <input type="file" accept="image/*" ref="fileDesktop" @change="onDesktopChosen" />
        </div>
        <div class="col-sm-6">
          <label>Imagem mobile <small class="text-muted">(opcional — sem ela, usa a desktop)</small></label>
          <div class="banner-drop" @click="pickMobile">
            <img v-if="newBanner.previewMobile" :src="newBanner.previewMobile" class="img-fluid" />
            <div v-else class="banner-drop-hint">
              <i class="mdi mdi-cellphone font-size-24"></i>
              <span>Selecionar imagem<br/><small>{{ positionInfo(newBanner.position).mob }}</small></span>
            </div>
          </div>
          <input type="file" accept="image/*" ref="fileMobile" @change="onMobileChosen" />
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-sm-6">
          <div class="form-group">
            <label>Link <small class="text-muted">(rota interna ou URL)</small></label>
            <input type="text" class="form-control" v-model="newBanner.link" placeholder="/search?categoria=... ou https://..." />
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label>Texto alternativo <small class="text-muted">(acessibilidade)</small></label>
            <input type="text" class="form-control" v-model="newBanner.alt" placeholder="Descrição da imagem" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <div class="form-group">
            <label>Exibir a partir de <small class="text-muted">(opcional)</small></label>
            <input type="date" class="form-control" v-model="newBanner.start_date" />
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label>Exibir até <small class="text-muted">(opcional)</small></label>
            <input type="date" class="form-control" v-model="newBanner.end_date" />
          </div>
        </div>
      </div>

      <hr />
      <b-form-checkbox v-model="newBanner.show_overlay" switch>
        <label class="mb-0">Texto sobre a imagem (overlay) — título vira o destaque; link vira o destino do botão</label>
      </b-form-checkbox>

      <div class="row mt-2" v-if="newBanner.show_overlay">
        <div class="col-sm-4">
          <div class="form-group">
            <label>Chapéu <small class="text-muted">(caps)</small></label>
            <input type="text" class="form-control" v-model="newBanner.eyebrow" placeholder="COLEÇÃO MEDITERRÂNEO" />
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label>Subtítulo</label>
            <input type="text" class="form-control" v-model="newBanner.subtitle" placeholder="Novidades em vidro soprado" />
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label>Texto do botão</label>
            <input type="text" class="form-control" v-model="newBanner.cta_label" placeholder="Ver a coleção" />
          </div>
        </div>
      </div>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-new')">Cancelar</b-btn>
        <b-btn variant="primary" :disabled="newBannerSaving" @click="createBanner">Salvar banner</b-btn>
      </div>
    </b-modal>

    <b-modal id="modal-config" title="Configurar Banner" title-class="font-18" centered>
      <div class="row">
        <div class="col-sm-7">
          <div class="form-group">
            <label>Título</label>
            <input type="text" class="form-control" v-model="bannerSelected.title" />
          </div>
        </div>
        <div class="col-sm-5">
          <div class="form-group">
            <label>Posição</label>
            <select class="form-control" v-model="bannerSelected.position">
              <option v-for="p in positions" :key="p.value" :value="p.value">{{ p.value }}</option>
            </select>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-7">
          <div class="form-group">
            <label>Link</label>
            <input type="text" class="form-control" v-model="bannerSelected.link" placeholder="/search?... ou https://..." />
          </div>
        </div>
        <div class="col-sm-5">
          <div class="form-group">
            <label>Texto alternativo</label>
            <input type="text" class="form-control" v-model="bannerSelected.alt" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6">
          <div class="form-group">
            <label>Exibir a partir de</label>
            <input type="date" class="form-control" v-model="bannerSelected.start_date" />
          </div>
        </div>
        <div class="col-sm-6">
          <div class="form-group">
            <label>Exibir até</label>
            <input type="date" class="form-control" v-model="bannerSelected.end_date" />
          </div>
        </div>
      </div>

      <b-form-checkbox v-model="bannerSelected.show_overlay" switch class="mb-2">
        <label class="mb-0">Texto sobre a imagem (overlay)</label>
      </b-form-checkbox>

      <div class="row" v-if="bannerSelected.show_overlay">
        <div class="col-sm-4">
          <div class="form-group">
            <label>Chapéu</label>
            <input type="text" class="form-control" v-model="bannerSelected.eyebrow" />
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label>Subtítulo</label>
            <input type="text" class="form-control" v-model="bannerSelected.subtitle" />
          </div>
        </div>
        <div class="col-sm-4">
          <div class="form-group">
            <label>Texto do botão</label>
            <input type="text" class="form-control" v-model="bannerSelected.cta_label" />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <label>Produtos</label>
          <multiselect
            v-model="bannerSelected.products"
            :options="products"
            :close-on-select="false"
            :clear-on-select="true"
            :loading="isLoadingProducts"
            :multiple="true"
            @search-change="findProducts"
            placeholder="Selecione o produto"
            label="fullDescription"
            track-by="_id"
            selectLabel="Enter para selecionar"
            selectedLabel="Selecionado"
            deselectLabel="Enter para remover"
          ></multiselect>
          <small class="text-muted">Sem link preenchido, o clique no banner busca estes produtos.</small>
        </div>
      </div>

      <div slot="modal-footer">
        <b-btn variant="link" @click="$bvModal.hide('modal-config')">Fechar</b-btn>
        <b-btn variant="primary" @click="configBanner">Confirmar</b-btn>
      </div>
    </b-modal>
  </Layout>
</template>

<style lang="scss">
  .card-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
  }

  .card-banner.card-body {
    padding-top: 0px;
  }

  img.greyscale {
    filter: grayscale(100%)
  }

  img.missing-web {
    opacity: 0.5;
  }

  .banners-drag {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;

    .image {
      display: flex;
      flex-direction: row;
      align-items: center;

      .image-box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .banner-drop {
    border: 2px dashed #ced4da;
    border-radius: 8px;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.2s;

    &:hover { border-color: #556ee6; }

    .banner-drop-hint {
      text-align: center;
      color: #74788d;
      padding: 16px;

      span { display: block; margin-top: 6px; font-size: 12.5px; }
    }
  }

  input[type="file"] {
      display: none;
    }
</style>
